export type OrganizationSummary = {
  organizationId: string;
  name: string;
  description?: string;
  role?: UserRoles;
  status?: string;
};

export type ConsentOption = 'identified' | 'decline';

// Legacy type - kept for backwards compatibility
export type EmailPreferences = {
  marketing?: boolean;
  reminders?: boolean;
  assignments?: boolean;
  organizational?: boolean;
};

// Channel preferences for each notification type
export type NotificationChannels = {
  mail?: boolean;
  badge?: boolean;
  slack?: boolean;
};

// Comprehensive notification preferences by category
export type NotificationPreferences = {
  // Marketing Notifications - Mail only (default: ON)
  marketing?: NotificationChannels;
  // Assignment Notifications - Mail, Badge, Slack (default: all ON)
  assignments?: NotificationChannels;
  // Execution Reminders - Mail, Badge, Slack (default: all ON)
  executionReminders?: NotificationChannels;
  // Organization Changes - Mail, Slack optional (default: Mail ON)
  organizationChanges?: NotificationChannels;
  // Discrepancies - Mail (default OFF), Badge (ON), Slack (ON)
  discrepancies?: NotificationChannels;
  // Wolf Pendings - Mail (default OFF), Badge (ON), Slack (default OFF)
  wolfPendings?: NotificationChannels;
  // Calendar Trigger Now - Mail (default OFF), Badge (default OFF), Slack (ON)
  calendarTrigger?: NotificationChannels;
};

export type TutorialPreferences = {
  completed?: boolean;
  dismissed?: boolean;
  version?: string;
  lastStep?: number;
};

export type ExecutionsViewMode = 'tabs' | 'stacked';

export type UserPreferences = {
  allowLogRocketRecording?: ConsentOption;
  emailPreferences?: EmailPreferences; // Legacy - kept for backwards compatibility
  notificationPreferences?: NotificationPreferences;
  tutorial?: TutorialPreferences;
  executionsViewMode?: ExecutionsViewMode;
  [key: string]: any;
};

export type User = {
  accessToken?: string;
  status?: UserStatus;
  organizationStatus?: UserStatus;
  file?: string;
  email?: string;
  userId?: string;
  firstName?: string;
  lastName?: string;
  avatar?: string;
  role?: UserRoles;
  lastLogin?: string;
  failedLoginAttempts?: number;
  loginMethod?: string;
  organizationId?: string;
  defaultOrganizationId?: string;
  rememberMe?: boolean;
  locale?: UserLocale;
  organizations?: OrganizationSummary[];
  seatAssignmentId?: string;
  seatType?: string;
  hasSeat?: boolean;
  preferences?: UserPreferences;
};

export interface createOrUpdateUserRequest {
  createUser?: User;
  createUserByEmail?: User;
  updateOtherUser?: User;
}

export interface updateOwnUserRequest {
  updateOwnUser: User;
}

export interface updateDefaultOrganizationRequest {
  updateDefaultOrganization: User;
}

export interface updateUserPreferencesRequest {
  updateUserPreferences: User;
}

export interface disableOrEnableUserRequest {
  disableUser?: User;
  enableUser?: User;
}

export interface getSelfWithOrganizationRequest {
  getSelfWithOrganization: {
    user: User;
    organization: {
      name: string;
      organizationId: string;
      description?: string;
      onboardingDetails?: any;
      context?: any;
      lastProfileUpdate?: Date;
      pendings?: any[];
    };
    role: UserRoles;
    organizations: OrganizationSummary[];
  };
}

export interface getUsersRequest {
  getUsers: User[];
}

export interface getUserByEmailRequest {
  getUserByEmail: User;
}

export interface getPaginatedUsersRequest {
  getPaginatedUsers: {
    users: User[];
    count: number;
  };
}

export interface getOwnerFromOrganizationRequest {
  getOwnerFromOrganization: {
    user: User;
  }
}

export interface getOrganizationUsersEmailSimpleRequest {
  getOrganizationUsersEmail: User[];
}

export interface getAssignableUsersRequest {
  getAssignableUsers: User[];
}

export interface getLastMessagesRequest {
  getLastMessages: any[];
}

export interface UserOrganizationDto {
  userOrganizationId: string;
  userId: string;
  organizationId: string;
  role: UserRoles;
  status?: UserStatus;
  createdAt?: Date;
  updatedAt?: Date;
  deletedAt?: Date;
  organization: {
    organizationId: string;
    name: string;
    description?: string;
  };
  user?: {
    userId: string;
    firstName?: string;
    lastName?: string;
    email: string;
  };
}

export interface getUserOrganizationsRequest {
  getUserOrganizations: UserOrganizationDto[];
}

export interface getOrganizationUsersRequest {
  getOrganizationUsers: UserOrganizationDto[];
}

export interface addUserToOrganizationRequest {
  addUserToOrganization: UserOrganizationDto;
}

export interface updateUserOrganizationRoleRequest {
  updateUserOrganizationRole: UserOrganizationDto;
}

export interface removeUserFromOrganizationRequest {
  removeUserFromOrganization: boolean;
}

export interface getUserOrganizationsWithDeletedRequest {
  getUserOrganizationsWithDeleted: UserOrganizationDto[];
}

export interface leaveOrganizationRequest {
  leaveOrganization: boolean;
}

export interface restoreUserToOrganizationRequest {
  restoreUserToOrganization: UserOrganizationDto | null;
}

export enum UserRoles {
  SUPER_ADMIN = 'SUPER_ADMIN',
  OWNER = 'OWNER',
  ADMIN = 'ADMIN',
  USER = 'USER',
}

export enum UserStatus {
  ACTIVE = 'ACTIVE',
  DISABLED = 'DISABLED',
}

export enum UserLocale {
  EN = 'en',
  ES = 'es',
}
